import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CharacterCard from "@/components/CharacterCard";
import GalleryGrid from "@/components/GalleryGrid";
import CommentSection from "@/components/CommentSection";
import UniverseStore from "@/components/UniverseStore";
import SEOHead from "@/components/SEOHead";
import ShareButtons from "@/components/ShareButtons";
import { useTranslateContent } from "@/hooks/useTranslateContent";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Heart, HeartOff, Calendar, Clock, Users, Map, Swords, 
  Gamepad2, Music, Link2, ImageIcon, ChevronLeft, Search, Loader2, Sparkles, History, Film, Download
} from "lucide-react";

const UniversePage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [universe, setUniverse] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [maps, setMaps] = useState([]);
  const [weapons, setWeapons] = useState([]);
  const [games, setGames] = useState([]);
  const [music, setMusic] = useState([]);
  const [sources, setSources] = useState([]);
  const [filmography, setFilmography] = useState([]);
  const [comments, setComments] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const universeRes = await axios.get(`${API}/universes/${slug}`);
      setUniverse(universeRes.data);
      const universeId = universeRes.data.id;
      
      // Track view
      axios.post(`${API}/view/universe/${universeId}`).catch(() => {});

      // Fetch all related data in parallel
      const [charsRes, galleryRes, mapsRes, weaponsRes, gamesRes, musicRes, sourcesRes, filmRes, commentsRes] = await Promise.all([
        axios.get(`${API}/characters?universe_id=${universeId}`),
        axios.get(`${API}/gallery?universe_id=${universeId}`),
        axios.get(`${API}/maps?universe_id=${universeId}`),
        axios.get(`${API}/weapons?universe_id=${universeId}`),
        axios.get(`${API}/games?universe_id=${universeId}`),
        axios.get(`${API}/music?universe_id=${universeId}`),
        axios.get(`${API}/sources?universe_id=${universeId}`),
        axios.get(`${API}/filmography?universe_id=${universeId}`),
        axios.get(`${API}/comments?target_type=universe&target_id=${universeId}`),
      ]);

      setCharacters(charsRes.data);
      setGallery(galleryRes.data);
      setMaps(mapsRes.data);
      setWeapons(weaponsRes.data);
      setGames(gamesRes.data);
      setMusic(musicRes.data);
      setSources(sourcesRes.data);
      setFilmography(filmRes.data);
      setComments(commentsRes.data);

      // Check if favorited
      if (user) {
        try {
          const favRes = await axios.get(`${API}/favorites`);
          const fav = favRes.data.find(f => f.target_type === "universe" && f.target_id === universeId);
          setIsFavorite(!!fav);
        } catch (e) {
          // Ignore
        }
      }
    } catch (error) {
      console.error("Failed to fetch universe:", error);
      toast.error("Failed to load universe");
    } finally {
      setLoading(false);
    }
  }, [slug, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleFavorite = async () => {
    if (!user) {
      toast.error("Please sign in to save favorites");
      return;
    }
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/by-target/universe/${universe.id}`);
        setIsFavorite(false);
        toast.success("Removed from favorites");
      } else {
        await axios.post(`${API}/favorites`, {
          target_type: "universe",
          target_id: universe.id,
        });
        setIsFavorite(true);
        toast.success("Added to favorites");
      }
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  const handleCommentAdded = (comment) => {
    setComments([comment, ...comments]);
  };

  const handleCommentDeleted = (commentId) => {
    setComments(comments.filter(c => c.id !== commentId));
  };

  const { translated: translatedDesc, loading: translatingDesc } = useTranslateContent(
    universe?.description,
    universe?.id ? `universe-desc-${universe.id}` : ""
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030712]">
        <Navbar />
        <div className="spinner"></div>
      </div>
    );
  }

  if (!universe) {
    return (
      <div className="min-h-screen flex flex-col bg-[#030712]">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl text-white mb-4">Universe not found</h1>
            <Link to="/" className="text-yellow-500 hover:underline">Return home</Link>
          </div>
        </div>
      </div>
    );
  }

  const tabItems = [
    { id: "overview", label: t("universe.overview"), icon: Calendar },
    { id: "characters", label: t("universe.characters"), icon: Users, count: characters.length },
    { id: "filmography", label: t("universe.filmography"), icon: Film, count: filmography.length },
    { id: "gallery", label: t("universe.gallery"), icon: ImageIcon, count: gallery.length },
    { id: "maps", label: t("universe.maps"), icon: Map, count: maps.length },
    { id: "items", label: t("universe.itemsCreatures"), icon: Swords, count: weapons.length },
    { id: "games", label: t("universe.games"), icon: Gamepad2, count: games.length },
    { id: "music", label: t("universe.music"), icon: Music, count: music.length },
    { id: "sources", label: t("universe.sources"), icon: Link2, count: sources.length },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#030712]">
      <SEOHead
        title={universe.name}
        description={universe.description?.slice(0, 160)}
        image={universe.cover_image}
        type="article"
      />
      <Navbar />
      
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-end pt-16" data-testid="universe-hero">
        <div className="absolute inset-0">
          <img 
            src={universe.cover_image || "https://static.prod-images.emergentagent.com/jobs/f60715c2-3b6b-4c99-a6c8-5c96b98e6cf9/images/53108f7d8ec9c28606e03e3c421e51fe4b80f13705cd9eeafc38c88182bd88ad.png"}
            alt={universe.name}
            className="w-full h-full object-cover"
          />
          <div className="hero-gradient absolute inset-0" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors">
            <ChevronLeft className="w-4 h-4" />
            Back to Universes
          </Link>
          
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <span className={`inline-block px-3 py-1 text-xs font-semibold uppercase tracking-wider mb-4 badge-${universe.category}`}>
                {universe.category}
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white font-['Outfit'] text-glow">
                {universe.name}
              </h1>
              <div className="flex items-center gap-6 mt-4 text-sm text-slate-400">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Est. {universe.formed_date}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {universe.time_period}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <ShareButtons title={`${universe.name} — FanVerse`} description={universe.description} />
              <Link
                to={`/lore-explorer?universe=${universe.slug}`}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/15 border border-amber-500/25 text-amber-400 text-xs sm:text-sm hover:bg-amber-500/25 transition-colors"
                data-testid="ask-lore-oracle"
              >
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline">Ask</span> Lore Oracle
              </Link>
              <Link
                to={`/timeline/${universe.slug}`}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-sky-500/15 border border-sky-500/25 text-sky-400 text-xs sm:text-sm hover:bg-sky-500/25 transition-colors"
                data-testid="view-timeline"
              >
                <History className="w-4 h-4" />
                Timeline
              </Link>
              <Button
                onClick={toggleFavorite}
                className={`text-xs sm:text-sm ${isFavorite ? "bg-red-500 hover:bg-red-600" : "btn-secondary"}`}
                data-testid="favorite-button"
              >
                {isFavorite ? <HeartOff className="w-4 h-4 mr-2" /> : <Heart className="w-4 h-4 mr-2" />}
                {isFavorite ? t("universe.removeFavorite") : t("universe.addToFavorites")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="flex-1 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="flex overflow-x-auto gap-1 sm:gap-2 bg-transparent border-b border-white/10 pb-4 mb-8 justify-start scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
              {tabItems.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap data-[state=active]:text-yellow-500 data-[state=active]:border-b-2 data-[state=active]:border-yellow-500 text-slate-400 hover:text-white transition-colors bg-transparent flex-shrink-0"
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(" ")[0]}</span>
                  {tab.count !== undefined && <span className="text-xs opacity-60">({tab.count})</span>}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-8">
              <div className="card-surface p-8">
                <h2 className="text-2xl font-bold text-white mb-4 font-['Outfit']">About {universe.name}</h2>
                <p className="text-slate-300 leading-relaxed whitespace-pre-line">{translatedDesc || universe.description}</p>
                {translatingDesc && <span className="text-xs text-amber-500/50 mt-2 block">Translating...</span>}
              </div>

              {/* Universe Images Gallery */}
              {universe.images?.length > 0 && (
                <div className="card-surface p-6">
                  <h3 className="text-xs font-mono tracking-[0.2em] uppercase text-amber-500 mb-4">{t("universe.images")}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {universe.images.map((img, i) => (
                      <div key={i} className="aspect-video rounded-lg overflow-hidden border border-white/5">
                        <img src={img} alt={`${universe.name} ${i+1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Shop This Universe */}
              <UniverseStore universeId={universe.id} universeName={universe.name} />
              
              <CommentSection
                targetType="universe"
                targetId={universe.id}
                comments={comments}
                onCommentAdded={handleCommentAdded}
                onCommentDeleted={handleCommentDeleted}
              />
            </TabsContent>

            {/* Characters Tab */}
            <TabsContent value="characters">
              {characters.length === 0 ? (
                <p className="text-slate-500">{t("universe.noCharacters")}</p>
              ) : (
                <>
                  {user?.role === "admin" && (
                  <div className="flex justify-end mb-4">
                    <a
                      href={`${API}/export/characters/${slug}`}
                      download
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 text-xs sm:text-sm hover:bg-emerald-500/25 transition-colors"
                      data-testid="download-characters-xlsx"
                    >
                      <Download className="w-4 h-4" />
                      Download Spreadsheet
                    </a>
                  </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {characters.map((character) => (
                      <CharacterCard key={character.id} character={character} />
                    ))}
                  </div>
                </>
              )}
            </TabsContent>

            {/* Filmography Tab */}
            <TabsContent value="filmography">
              {filmography.length === 0 ? (
                <p className="text-slate-500">{t("universe.noFilmography")}</p>
              ) : (
                <div className="space-y-6">
                  {/* Movies */}
                  {filmography.filter(f => f.media_type === "movie").length > 0 && (
                    <div>
                      <h3 className="text-lg font-bold text-white mb-4 font-['Outfit'] flex items-center gap-2">
                        <Film className="w-5 h-5 text-amber-500" /> {t("universe.movies")}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {filmography.filter(f => f.media_type === "movie").map((film) => {
                          const Wrapper = film.imdb_url ? "a" : "div";
                          const linkProps = film.imdb_url ? { href: film.imdb_url, target: "_blank", rel: "noopener noreferrer" } : {};
                          return (
                            <Wrapper key={film.id} {...linkProps} className="card-surface p-4 hover:border-amber-500/30 transition-all group block" data-testid={`film-${film.id}`}>
                              <div className="flex items-start gap-4">
                                <div className="w-16 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-amber-500/5">
                                  {film.image ? (
                                    <img src={film.image} alt={film.title} className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                                  ) : null}
                                  <div className={`w-full h-full items-center justify-center ${film.image ? 'hidden' : 'flex'}`}>
                                    <Film className="w-6 h-6 text-amber-500/50" />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <h4 className="text-white font-semibold font-['Outfit'] truncate group-hover:text-amber-400 transition-colors">{film.title}</h4>
                                    {film.year && <span className="text-xs font-mono text-amber-500/70 flex-shrink-0">{film.year}</span>}
                                    {film.imdb_url && <span className="text-[10px] font-bold text-yellow-400 bg-yellow-400/10 px-1.5 py-0.5 rounded flex-shrink-0" data-testid={`imdb-badge-${film.id}`}>IMDb</span>}
                                    {film.rt_score != null && (
                                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${film.rt_score >= 60 ? 'text-red-400 bg-red-500/10' : 'text-green-400 bg-green-500/10'}`} data-testid={`rt-score-${film.id}`}>
                                        {film.rt_score >= 60 ? '\uD83C\uDF45' : '\uD83E\uDD6C'} {film.rt_score}%
                                      </span>
                                    )}
                                  </div>
                                  {film.director && <p className="text-xs text-slate-500 mb-1">{t("universe.directedBy")} {film.director}</p>}
                                  {film.stars?.length > 0 && <p className="text-xs text-slate-400 mb-2 truncate">{film.stars.join(", ")}</p>}
                                  {film.description && <p className="text-slate-400 text-sm line-clamp-2">{film.description}</p>}
                                </div>
                              </div>
                            </Wrapper>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* TV Shows */}
                  {filmography.filter(f => f.media_type === "tv_show").length > 0 && (
                    <div>
                      <h3 className="text-lg font-bold text-white mb-4 font-['Outfit'] flex items-center gap-2">
                        <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="2" y="7" width="20" height="15" rx="2" /><path d="M17 2l-5 5-5-5" /></svg>
                        {t("universe.tvShows")}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {filmography.filter(f => f.media_type === "tv_show").map((show) => {
                          const Wrapper = show.imdb_url ? "a" : "div";
                          const linkProps = show.imdb_url ? { href: show.imdb_url, target: "_blank", rel: "noopener noreferrer" } : {};
                          return (
                            <Wrapper key={show.id} {...linkProps} className="card-surface p-4 hover:border-purple-500/30 transition-all group block" data-testid={`film-${show.id}`}>
                              <div className="flex items-start gap-4">
                                <div className="w-16 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-purple-500/5">
                                  {show.image ? (
                                    <img src={show.image} alt={show.title} className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                                  ) : null}
                                  <div className={`w-full h-full items-center justify-center ${show.image ? 'hidden' : 'flex'}`}>
                                    <svg className="w-6 h-6 text-purple-400/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="2" y="7" width="20" height="15" rx="2" /><path d="M17 2l-5 5-5-5" /></svg>
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <h4 className="text-white font-semibold font-['Outfit'] truncate group-hover:text-purple-300 transition-colors">{show.title}</h4>
                                    {show.year && <span className="text-xs font-mono text-purple-400/70 flex-shrink-0">{show.year}</span>}
                                    {show.imdb_url && <span className="text-[10px] font-bold text-yellow-400 bg-yellow-400/10 px-1.5 py-0.5 rounded flex-shrink-0" data-testid={`imdb-badge-${show.id}`}>IMDb</span>}
                                    {show.rt_score != null && (
                                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${show.rt_score >= 60 ? 'text-red-400 bg-red-500/10' : 'text-green-400 bg-green-500/10'}`} data-testid={`rt-score-${show.id}`}>
                                        {show.rt_score >= 60 ? '\uD83C\uDF45' : '\uD83E\uDD6C'} {show.rt_score}%
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 mb-1">
                                    {show.director && <p className="text-xs text-slate-500">{t("universe.createdBy")} {show.director}</p>}
                                    {show.seasons && <span className="text-xs font-mono text-slate-500">{show.seasons} {show.seasons === 1 ? t("universe.season") : t("universe.seasons")}</span>}
                                  </div>
                                  {show.stars?.length > 0 && <p className="text-xs text-slate-400 mb-2 truncate">{show.stars.join(", ")}</p>}
                                  {show.description && <p className="text-slate-400 text-sm line-clamp-2">{show.description}</p>}
                                </div>
                              </div>
                            </Wrapper>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Gallery Tab */}
            <TabsContent value="gallery">
              <GalleryGrid items={gallery} />
              
              {/* Discover More Media Section */}
              <DiscoverMedia universeName={universe.name} universeId={universe.id} onImported={() => fetchData()} />
            </TabsContent>

            {/* Maps Tab */}
            <TabsContent value="maps">
              {maps.length === 0 ? (
                <p className="text-slate-500">{t("universe.noMaps")}</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {maps.map((map) => (
                    <div key={map.id} className="card-surface overflow-hidden">
                      <img src={map.image} alt={map.name} className="w-full aspect-video object-cover" />
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-white mb-2 font-['Outfit']">{map.name}</h3>
                        <p className="text-slate-400 text-sm">{map.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Items & Creatures Tab */}
            <TabsContent value="items">
              {weapons.length === 0 ? (
                <p className="text-slate-500">{t("universe.noItems")}</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {weapons.map((item) => (
                    <div key={item.id} className="card-surface p-6">
                      <span className="text-xs font-mono uppercase tracking-wider text-amber-500">{item.type}</span>
                      <h3 className="text-lg font-bold text-white mt-2 mb-2 font-['Outfit']">{item.name}</h3>
                      <p className="text-slate-400 text-sm">{item.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Games Tab */}
            <TabsContent value="games">
              {games.length === 0 ? (
                <p className="text-slate-500">{t("universe.noGames")}</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {games.map((game) => (
                    <div key={game.id} className="card-surface p-6">
                      <span className="text-xs font-mono uppercase tracking-wider text-amber-500">{game.type}</span>
                      <h3 className="text-lg font-bold text-white mt-2 mb-2 font-['Outfit']">{game.name}</h3>
                      <p className="text-slate-400 text-sm mb-4">{game.description}</p>
                      {game.link && (
                        <a href={game.link} target="_blank" rel="noopener noreferrer" className="text-yellow-500 text-sm hover:underline">
                          {t("universe.learnMore")} →
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Music Tab */}
            <TabsContent value="music">
              {music.length === 0 ? (
                <p className="text-slate-500">{t("universe.noMusic")}</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {music.map((track) => (
                    <div key={track.id} className="card-surface p-6 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                        <Music className="w-6 h-6 text-yellow-500" />
                      </div>
                      <div className="flex-1">
                        <span className="text-xs font-mono uppercase tracking-wider text-amber-500">{track.type}</span>
                        <h3 className="text-lg font-bold text-white font-['Outfit']">{track.name}</h3>
                        {track.description && <p className="text-slate-400 text-sm">{track.description}</p>}
                      </div>
                      {track.link && (
                        <a href={track.link} target="_blank" rel="noopener noreferrer" className="btn-secondary px-4 py-2 text-sm">
                          {t("universe.listen")}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Sources Tab */}
            <TabsContent value="sources">
              {sources.length === 0 ? (
                <p className="text-slate-500">{t("universe.noSources")}</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sources.map((source) => (
                    <a
                      key={source.id}
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="card-surface p-6 flex items-center gap-4 hover:border-yellow-500/30"
                    >
                      <div className="w-12 h-12 rounded-full bg-sky-500/20 flex items-center justify-center">
                        <Link2 className="w-6 h-6 text-sky-500" />
                      </div>
                      <div className="flex-1">
                        <span className="text-xs font-mono uppercase tracking-wider text-amber-500">{source.type}</span>
                        <h3 className="text-lg font-bold text-white font-['Outfit']">{source.name}</h3>
                        {source.description && <p className="text-slate-400 text-sm">{source.description}</p>}
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const DiscoverMedia = ({ universeName, universeId, onImported }) => {
  const { user } = useAuth();
  const [query, setQuery] = useState(universeName || "");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const response = await axios.post(`${API}/media/search`, {
        query: query,
        media_type: "image",
        per_page: 8,
      });
      setResults(response.data.results);
      setExpanded(true);
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleImport = async (item) => {
    if (!user || user.role !== "admin") {
      toast.error("Admin access required to import media");
      return;
    }
    try {
      await axios.post(`${API}/gallery/import`, {
        universe_id: universeId,
        title: item.title,
        description: `Discovered via search for "${universeName}"`,
        media_type: "image",
        url: item.url,
        source: item.source,
      });
      toast.success("Added to gallery!");
      if (onImported) onImported();
    } catch (error) {
      toast.error("Failed to import");
    }
  };

  return (
    <div className="mt-10 border-t border-white/5 pt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-mono tracking-wider uppercase text-slate-500">Discover More Media</h3>
        {!expanded && (
          <Button variant="ghost" size="sm" onClick={() => setExpanded(true)} className="text-yellow-500 text-xs" data-testid="expand-discover">
            <Search className="w-3.5 h-3.5 mr-1.5" /> Search Images
          </Button>
        )}
      </div>
      
      {expanded && (
        <>
          <div className="flex gap-3 mb-6">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search images related to ${universeName}...`}
              className="input-dark flex-1"
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              data-testid="discover-search-input"
            />
            <Button onClick={handleSearch} disabled={searching} className="btn-primary" data-testid="discover-search-btn">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          
          {results.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {results.map((item) => (
                <div key={item.id} className="group relative aspect-video overflow-hidden rounded-lg border border-white/5 hover:border-yellow-500/30 transition-all">
                  <img src={item.thumbnail || item.url} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-2">
                      <p className="text-white text-xs truncate mb-1">{item.title}</p>
                      {user?.role === "admin" && (
                        <button onClick={() => handleImport(item)} className="text-yellow-500 text-xs hover:underline" data-testid={`discover-import-${item.id}`}>
                          + Add to Gallery
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UniversePage;
